import { useCallback, useEffect, useState } from 'react';
import { familyPackingApi } from '../api/packing';
import type { PackingList } from '../api/packing';
import type { Child } from '../api/auth';
import type { RsvpSummary } from '../api/events';
import { Button } from '../ui/Button';
import { ItemBadge } from '../ui/ItemBadge';

/**
 * How many children's lists are on screen before the rest go behind a disclosure.
 *
 * Every real family is inside this, so in production it never fires. It exists because a screen
 * that renders every row it is given is a bug the dev database will show you, and this one does:
 * smoke residue has left one seeded parent with thirty-three children in a single unit. The rest
 * stay one tap away rather than truncated — hiding a child would hide a list somebody has to pack.
 */
const STACKED = 6;

/** How many not-going children are named before the line just counts them. */
const NAMEABLE = 3;

/**
 * What each of a parent's children is being asked to bring, on the event page they already read.
 *
 * **The fourth occurrence of the TROOP-15 shape.** `FamilyPackingEndpoints` has existed since
 * TROOP-68 and nothing in the app called it, so the routes written for the person who actually
 * packs the bag were reachable only by curl (TROOP-77). A guardian holds no membership, so
 * `UnitScopeMiddleware` 404s them on `/units/` before an endpoint runs — this is the door, not a
 * convenience.
 *
 * **Only children who answered Going.** Asking somebody to pack for a trip they are not on is
 * noise, and silence is not consent to pack either — a child who has not answered gets a sentence
 * saying so rather than an empty space that reads like a bug.
 *
 * **What the policy decides and this does not argue with.** A guardian sees what their child
 * sees: Unit-level lists by standing, and since TROOP-79 their child's subgroup list too. They
 * tick the rows with their own child's name on them (TROOP-78) and no others, because an
 * eleven-year-old is not the one loading the car. They may not *write* either list — a youth
 * Patrol Leader's office is theirs rather than their family's. The child's own list they write
 * outright. This renders what the endpoint returned and reaches for nothing more.
 */
export function FamilyPacking({
  eventId,
  children,
  rsvps,
  onChanged,
}: {
  eventId: string;
  /** This parent's children who are in this event's unit. */
  children: Child[];
  /**
   * Who is going, when the caller already has it. Null since TROOP-116: the docket opens this
   * panel on demand, so the RSVP answers are no longer loaded up front by the screen above — and
   * fetching them here to grey out a child who said no would cost a request to render less.
   */
  rsvps: RsvpSummary | null;
  /** Tells the docket row above to re-read its counts. */
  onChanged?: () => void;
}) {
  const [lists, setLists] = useState<PackingList[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    try {
      setLists(await familyPackingApi.forEvent(eventId));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load what to bring.');
    }
  }, [eventId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);

    try {
      await action();
      await load();
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  const all = lists ?? [];
  const going = children.filter(
    (c) => rsvps?.rows.find((r) => r.personId === c.personId)?.response === 'Going',
  );
  const waiting = children.filter((c) => !going.some((g) => g.personId === c.personId));

  // Nothing at all until the first load lands: an empty card that fills in is worse than a beat
  // of nothing, and this section is never the reason somebody opened the page.
  if (lists === null && error === null) {
    return null;
  }

  const shown = showAll ? going : going.slice(0, STACKED);

  return (
    // No heading: the docket row above is the heading. It says "Bring" and this panel is what it
    // opens -- the product's own words for the feature, which are never "packing" on screen.
    <section>

      {error && (
        <p
          className="t-caption"
          role="alert"
          style={{ color: 'var(--color-status-blocked)', margin: '0 0 var(--space-3)' }}
        >
          {error}
        </p>
      )}

      {going.length === 0 ? (
        <p className="t-body" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
          {children.length === 1
            ? `Say ${children[0].displayName} is going and their list will appear here.`
            : 'Say who is going and their lists will appear here.'}
        </p>
      ) : (
        // Stacked, not tabbed. A parent packing two bags on a Thursday evening is working through
        // both, not choosing between them, and a tab bar hides the second one behind a click that
        // says nothing about what is in it.
        <div className="flex flex-col" style={{ gap: 'var(--space-4)' }}>
          {shown.map((child) => (
            <ChildKit
              key={child.personId}
              child={child}
              eventId={eventId}
              lists={all}
              busy={busy}
              onRun={run}
            />
          ))}

          {going.length > shown.length && (
            <div>
              <Button variant="secondary" onClick={() => setShowAll(true)}>
                Show {going.length - shown.length} more
              </Button>
            </div>
          )}
        </div>
      )}

      {waiting.length > 0 && going.length > 0 && (
        <p
          className="t-caption"
          style={{ color: 'var(--color-text-muted)', margin: 'var(--space-3) 0 0' }}
        >
          {/* Named while naming is useful, counted when it stops being -- the trade
              ParticipantStrip makes, for the same reason. */}
          {waiting.length <= NAMEABLE
            ? `${waiting.map((c) => c.displayName).join(', ')} ${
                waiting.length === 1 ? 'is' : 'are'
              } not down as going yet, so there is nothing to pack for them.`
            : `${waiting.length} others are not down as going yet, so there is nothing to pack for them.`}
        </p>
      )}
    </section>
  );
}

/** One child's list, and the gear the unit has their name against. */
function ChildKit({
  child,
  eventId,
  lists,
  busy,
  onRun,
}: {
  child: Child;
  eventId: string;
  lists: PackingList[];
  busy: boolean;
  onRun: (action: () => Promise<unknown>) => Promise<void>;
}) {
  const mine = lists.find((l) => l.forPersonId === child.personId);
  const suggestion = lists.find((l) => l.level === 'Personal' && l.forPersonId === null);

  // What the unit and their group have put this child's name against, kept apart because they are
  // different jobs: forgetting your own sleeping bag is not the same as leaving a patrol without a
  // stove.
  const assignedAt = (level: string) =>
    lists
      .filter((l) => l.forPersonId === null && l.level === level)
      .flatMap((l) =>
        l.items
          .filter((i) => i.assignedToPersonId === child.personId)
          .map((item) => ({ listId: l.id, listName: l.name, item })),
      );

  const forUnit = assignedAt('Unit');
  const forGroup = assignedAt('Subgroup');

  return (
    <div
      style={{
        background: 'var(--color-surface-raised)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-4)',
      }}
    >
      <h3 className="t-body-strong" style={{ color: 'var(--color-text-primary)', margin: 0 }}>
        What {child.displayName} is bringing
      </h3>
      <p className="t-caption" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
        Yours and theirs. Their leaders see how far along they are and not what is on it.
      </p>

      {[
        { heading: 'For the unit', rows: forUnit },
        { heading: `For ${child.displayName}'s group`, rows: forGroup },
      ].map(({ heading, rows }) =>
        rows.length === 0 ? null : (
          <div key={heading}>
            <h4
              className="t-label"
              style={{ color: 'var(--color-text-muted)', margin: 'var(--space-4) 0 0' }}
            >
              {heading}
            </h4>
            <ul style={{ listStyle: 'none', margin: 'var(--space-2) 0 0', padding: 0 }}>
              {rows.map(({ listId, listName, item }) => (
              <li
                key={item.id}
                className="flex items-center gap-2"
                style={{ borderTop: '1px solid var(--color-border-subtle)' }}
              >
                {/* Tickable, and it took a policy change rather than a prop: CanCheckItem names
                    the guardian now, so the parent doing the packing can say the propane is in
                    the car. The row still records who ticked it (TROOP-78). */}
                <label
                  className="flex flex-1 items-center gap-3"
                  style={{
                    paddingBlock: 'var(--space-2)',
                    minHeight: 'var(--tap-min)',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={item.isChecked}
                    disabled={busy}
                    onChange={(e) =>
                      onRun(() => familyPackingApi.check(listId, item.id, e.target.checked))
                    }
                    style={{ width: 20, height: 20 }}
                  />
                  <span
                    className="t-body"
                    style={{
                      color: item.isChecked
                        ? 'var(--color-text-muted)'
                        : 'var(--color-text-primary)',
                      textDecoration: item.isChecked ? 'line-through' : undefined,
                    }}
                  >
                    {item.text}
                    <span className="t-caption" style={{ color: 'var(--color-text-muted)' }}>
                      {' '}
                      &middot; {listName}
                    </span>
                    {item.isUnitOwned && (
                      <>
                        {' '}
                        <ItemBadge />
                      </>
                    )}
                  </span>
                </label>
                </li>
              ))}
            </ul>
          </div>
        ),
      )}

      <h4
        className="t-label flex flex-wrap items-baseline gap-2"
        style={{ color: 'var(--color-text-muted)', margin: 'var(--space-4) 0 0' }}
      >
        Theirs
        {mine && (
          <span className="t-caption tabular" style={{ textTransform: 'none', letterSpacing: 0 }}>
            {mine.checkedCount} of {mine.items.length} sorted
          </span>
        )}
      </h4>

      {!mine ? (
        // Two ways in rather than the Scout's three: their own saved lists are theirs and follow
        // them between units, and there is no guardian route that reads somebody else's -- which
        // is the containment working rather than a gap to plug here.
        <div className="flex flex-wrap gap-2" style={{ marginTop: 'var(--space-3)' }}>
          {suggestion && (
            <Button
              variant="primary"
              disabledReason={busy ? 'One moment.' : undefined}
              onClick={() =>
                onRun(() =>
                  familyPackingApi.startForChild(eventId, child.personId, 'EventSuggestion'),
                )
              }
            >
              Use the unit&rsquo;s list
            </Button>
          )}

          <Button
            variant="secondary"
            disabledReason={busy ? 'One moment.' : undefined}
            onClick={() =>
              onRun(() => familyPackingApi.startForChild(eventId, child.personId, 'Blank'))
            }
          >
            Start blank
          </Button>
        </div>
      ) : (
        <ul style={{ listStyle: 'none', margin: 'var(--space-2) 0 0', padding: 0 }}>
          {mine.items.map((item) => (
            <li
              key={item.id}
              className="flex items-center gap-2"
              style={{ borderTop: '1px solid var(--color-border-subtle)' }}
            >
              <label
                className="flex flex-1 items-center gap-3"
                style={{
                  paddingBlock: 'var(--space-2)',
                  minHeight: 'var(--tap-min)',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={item.isChecked}
                  disabled={busy}
                  onChange={(e) =>
                    onRun(() => familyPackingApi.check(mine.id, item.id, e.target.checked))
                  }
                  style={{ width: 20, height: 20 }}
                />
                <span
                  className="t-body"
                  style={{
                    color: item.isChecked ? 'var(--color-text-muted)' : 'var(--color-text-primary)',
                    textDecoration: item.isChecked ? 'line-through' : undefined,
                  }}
                >
                  {item.text}
                  {item.isOptional && (
                    <span className="t-caption" style={{ color: 'var(--color-text-muted)' }}>
                      {' '}
                      (optional)
                    </span>
                  )}
                  {item.isUnitOwned && (
                    <>
                      {' '}
                      <ItemBadge />
                    </>
                  )}
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
